import { useId, useMemo } from "react";
import { cn } from "../lib/cn";

// ============================================================================
// PhoneField — international phone input producing an E.164 string.
// ----------------------------------------------------------------------------
// A curated country-code selector (dial code + label) sits beside a national-
// number input. The component is CONTROLLED on a single E.164 string value
// (e.g. "+85291234567"): it splits that value into a dial code + national
// digits for display, and recombines on every edit. No external dependency —
// a curated dial-code list covers the markets ViccoFitness serves and keeps
// the bundle lean; `isE164` gives a basic shape check callers can reuse.
//
// Mobile-first + Pulse-styled (matches TextField/SelectField primitives) and
// accessible: a single visible label is wired to the national-number input,
// the country <select> carries its own aria-label, and hint/error are linked
// via aria-describedby. The "+" prefix is a decorative adornment.
// ============================================================================

export interface CountryDial {
  /** ISO 3166-1 alpha-2 (only used as a stable React key). */
  iso2: string;
  /** Dial code WITHOUT the leading "+", e.g. "852". */
  dial: string;
  /** Display label, e.g. "Hong Kong". */
  label: string;
}

/**
 * Curated dial-code list. Ordered with ViccoFitness's primary markets
 * (Hong Kong, mainland China, Taiwan, Macau) first, then a broad spread of
 * common international codes. Sorted-by-priority, not alphabetical, so the
 * likeliest pick is at the top of the picker. Longest dial codes must be
 * tried first when splitting an E.164 value (see `splitE164`).
 */
export const COUNTRY_DIALS: readonly CountryDial[] = [
  { iso2: "HK", dial: "852", label: "Hong Kong (+852)" },
  { iso2: "CN", dial: "86", label: "China (+86)" },
  { iso2: "TW", dial: "886", label: "Taiwan (+886)" },
  { iso2: "MO", dial: "853", label: "Macau (+853)" },
  { iso2: "SG", dial: "65", label: "Singapore (+65)" },
  { iso2: "MY", dial: "60", label: "Malaysia (+60)" },
  { iso2: "GB", dial: "44", label: "United Kingdom (+44)" },
  { iso2: "US", dial: "1", label: "United States / Canada (+1)" },
  { iso2: "AU", dial: "61", label: "Australia (+61)" },
  { iso2: "NZ", dial: "64", label: "New Zealand (+64)" },
  { iso2: "JP", dial: "81", label: "Japan (+81)" },
  { iso2: "KR", dial: "82", label: "South Korea (+82)" },
  { iso2: "TH", dial: "66", label: "Thailand (+66)" },
  { iso2: "PH", dial: "63", label: "Philippines (+63)" },
  { iso2: "ID", dial: "62", label: "Indonesia (+62)" },
  { iso2: "VN", dial: "84", label: "Vietnam (+84)" },
  { iso2: "IN", dial: "91", label: "India (+91)" },
  { iso2: "AE", dial: "971", label: "United Arab Emirates (+971)" },
  { iso2: "FR", dial: "33", label: "France (+33)" },
  { iso2: "DE", dial: "49", label: "Germany (+49)" },
  { iso2: "IT", dial: "39", label: "Italy (+39)" },
  { iso2: "ES", dial: "34", label: "Spain (+34)" },
  { iso2: "NL", dial: "31", label: "Netherlands (+31)" },
  { iso2: "CH", dial: "41", label: "Switzerland (+41)" },
  { iso2: "SE", dial: "46", label: "Sweden (+46)" },
  { iso2: "ZA", dial: "27", label: "South Africa (+27)" },
  { iso2: "BR", dial: "55", label: "Brazil (+55)" },
  { iso2: "MX", dial: "52", label: "Mexico (+52)" },
];

/** The default dial code (Hong Kong) when a value has no recognizable prefix. */
const DEFAULT_DIAL = "852";

/** Dial codes sorted longest-first so "+886…" matches before "+88…"/"+8…". */
const DIALS_BY_LENGTH = [...COUNTRY_DIALS].sort(
  (a, b) => b.dial.length - a.dial.length,
);

/** Strip everything except digits. */
function digitsOnly(s: string): string {
  return s.replace(/\D+/g, "");
}

/**
 * Basic E.164 shape check: a leading "+", a non-zero leading country digit,
 * and 1–15 digits total (the E.164 maximum). Deliberately permissive about
 * per-country length — it rejects obvious junk, not every invalid number.
 */
export function isE164(value: string): boolean {
  return /^\+[1-9]\d{1,14}$/.test(value.trim());
}

/**
 * Split a stored E.164 string into a known dial code + the national remainder.
 * Falls back to the default dial code (national part = all the digits) when no
 * curated prefix matches, so a hand-entered value never gets silently dropped.
 */
export function splitE164(value: string): { dial: string; national: string } {
  const digits = digitsOnly(value);
  if (!digits) return { dial: DEFAULT_DIAL, national: "" };
  for (const c of DIALS_BY_LENGTH) {
    if (digits.startsWith(c.dial)) {
      return { dial: c.dial, national: digits.slice(c.dial.length) };
    }
  }
  return { dial: DEFAULT_DIAL, national: digits };
}

/** Recombine a dial code + national digits into an E.164 string (or "" if empty). */
export function combineE164(dial: string, national: string): string {
  const nat = digitsOnly(national);
  if (!nat) return "";
  return `+${digitsOnly(dial)}${nat}`;
}

export interface PhoneFieldProps {
  label: string;
  /** Controlled E.164 value, e.g. "+85291234567" ("" when empty). */
  value: string;
  /** Receives the recombined E.164 string ("" when the national part clears). */
  onChange: (value: string) => void;
  /** Accessible label for the country-code <select>. */
  countryLabel: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  /** Autocomplete token for the national-number input (default "tel-national"). */
  autoComplete?: string;
}

const numberBase =
  "h-12 w-full rounded-md bg-surface pl-9 pr-4 text-base text-body placeholder:text-body-faint " +
  "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast " +
  "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none " +
  "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-60";

const selectBase =
  "h-12 w-full appearance-none rounded-md bg-surface pl-3 pr-8 text-base text-body " +
  "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast " +
  "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none " +
  "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-60";

/**
 * International phone input. Emits an E.164 string via `onChange`; the country
 * selector + national-number field are derived from the controlled `value`.
 */
export function PhoneField({
  label,
  value,
  onChange,
  countryLabel,
  hint,
  error,
  required,
  disabled,
  placeholder,
  id,
  autoComplete = "tel-national",
}: PhoneFieldProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errId = error ? `${fieldId}-err` : undefined;

  const { dial, national } = useMemo(() => splitE164(value), [value]);

  const handleDial = (nextDial: string) => {
    onChange(combineE164(nextDial, national));
  };
  const handleNational = (nextNational: string) => {
    onChange(combineE164(dial, nextNational));
  };

  return (
    <div className="w-full">
      <label
        htmlFor={fieldId}
        className="mb-1.5 block font-display text-sm font-bold text-body"
      >
        {label}
        {required && <span className="ml-1 text-ember">*</span>}
      </label>

      <div className="flex gap-2">
        {/* Country / dial-code selector — ~40% on mobile, fixed-ish on wider. */}
        <div className="relative w-2/5 max-w-[11rem] shrink-0">
          <select
            aria-label={countryLabel}
            aria-invalid={error ? true : undefined}
            value={dial}
            disabled={disabled}
            onChange={(e) => handleDial(e.target.value)}
            className={cn(selectBase, error && "ring-2 ring-danger focus:ring-danger")}
          >
            {COUNTRY_DIALS.map((c) => (
              <option key={c.iso2} value={c.dial}>
                {c.label}
              </option>
            ))}
          </select>
          <svg
            aria-hidden
            viewBox="0 0 20 20"
            className="pointer-events-none absolute right-2.5 top-1/2 h-5 w-5 -translate-y-1/2 text-body-muted"
            fill="none"
          >
            <path
              d="M5 7.5 10 12.5 15 7.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* National number, with a decorative "+" adornment. */}
        <div className="relative min-w-0 flex-1">
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-3 flex items-center font-medium text-body-muted"
          >
            +
          </span>
          <input
            id={fieldId}
            type="tel"
            inputMode="tel"
            autoComplete={autoComplete}
            required={required}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={cn(hintId, errId) || undefined}
            value={national}
            placeholder={placeholder}
            onChange={(e) => handleNational(e.target.value)}
            className={cn(numberBase, error && "ring-2 ring-danger focus:ring-danger")}
          />
        </div>
      </div>

      {error ? (
        <p id={errId} className="mt-1.5 text-sm font-medium text-danger">
          {error}
        </p>
      ) : (
        hint && (
          <p id={hintId} className="mt-1.5 text-sm text-body-muted">
            {hint}
          </p>
        )
      )}
    </div>
  );
}
PhoneField.displayName = "PhoneField";
